import { Button, Clickable, Col, Dropdown, Icon, Input, Radio, Row, Text } from 'components';
import { applyPointAdjustment, theme } from 'lib';
import { useEffect, useState } from 'react';
import GloryBoardCard from './GloryBoardCard';
import GloryBoardConfirmModal from './GloryBoardConfirmModal';
import {
  ACTION_OPTIONS,
  deriveAdjustment,
  isRetryable,
  newAdjustmentId,
  REASON_OPTIONS,
  REFUSAL_MESSAGES
} from './pointAdjustment';

// With one action available there is nothing to choose between, so the chooser is not shown and the
// action is fixed. A second action returning to ACTION_OPTIONS brings the radios back by itself.
const ONLY_ACTION = ACTION_OPTIONS.length === 1 ? ACTION_OPTIONS[0] : null;

// The shared Radio is a bare circle styled for dark backgrounds, so it needs a label around it.
const ActionOption = ({ option, checked, onSelect }) => (
  <Clickable onClick={onSelect}>
    <Row
      center
      noFlex
      gap="10px"
      marg="0 24px 0 0"
      role="radio"
      aria-checked={checked}
      style={{ alignItems: 'flex-start' }}
    >
      <Radio active={checked} activeColor={theme.SECONDARY} onClick={onSelect} />

      <Col noFlex>
        <Text med>{option.label}</Text>
        <Text c1 col={theme.GREY_60}>
          {option.description}
        </Text>
      </Col>
    </Row>
  </Clickable>
);

/** The outcome banner — the same shape whether the adjustment landed or was refused. */
const Outcome = ({ color, heading, detail }) => (
  <Col
    noFlex
    wid="100%"
    pad="12px 14px"
    marg="16px 0 0"
    bg={theme.GREY_10}
    hasRadius="10px"
    style={{ border: `1px solid ${color}`, boxSizing: 'border-box' }}
  >
    <Text med col={color}>
      {heading}
    </Text>

    {detail && (
      <Text c1 col={theme.GREY_60}>
        {detail}
      </Text>
    )}
  </Col>
);

const GloryBoardPointAdjustment = ({ participant, teamId, teamName, totalPoints, onApplied }) => {
  const [actionType, setActionType] = useState(ONLY_ACTION?.code || null);
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState(null);
  const [touchedPoints, setTouchedPoints] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [applied, setApplied] = useState(null);
  const [failure, setFailure] = useState(null);
  // Fixed at confirmation and held across retries of that confirmation, so a retry after an
  // unanswered request cannot subtract a second time.
  const [adjustmentId, setAdjustmentId] = useState(null);

  // A different fan is a different adjustment. Carrying an amount across would attach a number
  // composed for one person to another.
  useEffect(() => {
    setActionType(ONLY_ACTION?.code || null);
    setPoints('');
    setReason(null);
    setTouchedPoints(false);
    setConfirming(false);
    setApplied(null);
    setFailure(null);
    setAdjustmentId(null);
  }, [participant?.userId]);

  const draft = deriveAdjustment({
    participant,
    actionType,
    points,
    reasonCode: reason?.code,
    totalPoints
  });

  const actionLabel = ACTION_OPTIONS.find((a) => a.code === actionType)?.label;

  const handlePoints = (e) => {
    setTouchedPoints(true);
    setApplied(null);
    setPoints(e.target.value);
  };

  const openConfirmation = () => {
    // One identifier per confirmation. A retry inside this confirmation reuses it; starting a new
    // adjustment gets a new one, so two deliberate identical redemptions both land.
    setAdjustmentId(newAdjustmentId());
    setFailure(null);
    setConfirming(true);
  };

  const handleConfirm = async () => {
    setWaiting(true);
    setFailure(null);

    const res = await applyPointAdjustment({
      teamId,
      userId: participant.userId,
      adjustmentId,
      actionType,
      points: draft.pointsValue,
      reasonCode: reason.code
    });

    setWaiting(false);

    if (!res?.data) {
      // Each refusal names itself. Anything unrecognised is the system failing rather than a rule
      // being applied, and only that case is worth offering a retry for.
      const code = res?.code;
      setFailure({
        code,
        message: REFUSAL_MESSAGES[code] || 'Something went wrong and no points were changed.',
        retryable: isRetryable(code)
      });
      return;
    }

    setConfirming(false);
    setApplied(res.data);

    // The participant and the action stay so a second correction on the same fan needs no re-search.
    setPoints('');
    setReason(null);
    setTouchedPoints(false);
    setAdjustmentId(null);

    // The new balance comes from a refetch, never from arithmetic here: the server's figure is the
    // only one that is true, and the board's ordering has moved with it.
    onApplied?.();
  };

  return (
    <GloryBoardCard title="Adjust Points">
      <Text b2 col={theme.GREY_60}>
        Action
      </Text>

      {ONLY_ACTION ? (
        <Col
          noFlex
          wid="100%"
          pad="10px 12px"
          marg="6px 0 0"
          bg={theme.GREY_10}
          hasRadius="10px"
          style={{ boxSizing: 'border-box' }}
        >
          <Text med>{ONLY_ACTION.label}</Text>
          <Text c1 col={theme.GREY_60}>
            {ONLY_ACTION.description}
          </Text>
        </Col>
      ) : (
        <Row start noFlex marg="6px 0 0" rowWrap>
          {ACTION_OPTIONS.map((option) => (
            <ActionOption
              key={option.code}
              option={option}
              checked={actionType === option.code}
              onSelect={() => {
                setApplied(null);
                setActionType(option.code);
              }}
            />
          ))}
        </Row>
      )}

      {/* Amount and reason stack rather than sharing a line. The card is half the panel wide, and
          side by side left the reason trigger narrow enough that "Reward Redemption" wrapped onto
          two lines inside its own pill — a control that changes height when you choose from it. */}
      <Col start noFlex wid="100%" marg="20px 0 0">
        <Input
          title="Points"
          // DefaultInput hardcodes type="text" after its prop spread, so `type="number"` cannot
          // stick. inputMode still raises a numeric keypad, and letters now surface the real
          // validation message rather than being swallowed by the widget.
          inputMode="numeric"
          // Not 100%: the rounded Dropdown below draws its pill at `calc(100% - 34px)` while the
          // page is `box-sizing: border-box`, so its padding is subtracted twice and it lands 34px
          // short of its own container. Matching that here keeps the two fields the same length
          // and the same left edge; widening the dropdown instead would move every rounded
          // dropdown in the admin.
          wid="calc(100% - 34px)"
          placeholder="0"
          value={points}
          onChange={handlePoints}
          error={touchedPoints ? draft.errors.points : undefined}
        />

        <Dropdown
          admin
          rounded
          small
          marg="16px 0 0"
          title="Reason"
          wid="100%"
          placeholder="Select a reason"
          selected={reason}
          options={REASON_OPTIONS}
          handleSelect={(option) => {
            setApplied(null);
            setReason(option);
          }}
        />
      </Col>

      {/* What the balance is now and what this would make it, side by side — the administrator is
          about to act on the difference between the two, so neither figure stands alone. */}
      {draft.resultingBalance !== null && !draft.exceedsBalance && (
        <Row
          start
          center
          noFlex
          wid="100%"
          gap="12px"
          pad="12px 14px"
          marg="20px 0 0"
          bg={theme.GREY_10}
          hasRadius="10px"
          borderBox
        >
          <Col noFlex>
            <Text tiny col={theme.GREY_60}>
              Current
            </Text>
            <Text b2 med>
              {totalPoints.toLocaleString()}
            </Text>
          </Col>

          <Icon type="arrowRightLong" dimensions={14} col={theme.GREY_40} />

          <Col noFlex>
            <Text tiny col={theme.GREY_60}>
              New balance
            </Text>
            <Text h4 bold>
              {draft.resultingBalance.toLocaleString()}
            </Text>
          </Col>
        </Row>
      )}

      {draft.exceedsBalance && (
        <Text c1 col={theme.RED} marg="12px 0 0">
          {participant.name} holds {totalPoints.toLocaleString()} points. An adjustment cannot take
          a balance below zero.
        </Text>
      )}

      {applied && (
        <Outcome
          color={theme.GREEN}
          heading={`${applied.points.toLocaleString()} points redeemed`}
          detail={`New balance: ${applied.balanceAfter.toLocaleString()}`}
        />
      )}

      {failure && !confirming && <Outcome color={theme.RED} heading={failure.message} />}

      <Row noFlex wid="100%" justifyContent="flex-end" marg="20px 0 0">
        <Button
          text="Apply Redemption"
          onClick={openConfirmation}
          disabled={!draft.isComplete}
          style={{ width: 'fit-content' }}
        />
      </Row>

      {confirming && (
        <GloryBoardConfirmModal
          visible
          participant={participant}
          teamName={teamName}
          currentPoints={totalPoints}
          actionLabel={actionLabel}
          points={draft.pointsValue}
          reasonLabel={reason?.value}
          resultingBalance={draft.resultingBalance}
          waiting={waiting}
          failure={failure}
          onCancel={() => {
            setConfirming(false);
            setFailure(null);
          }}
          onConfirm={handleConfirm}
        />
      )}
    </GloryBoardCard>
  );
};

export default GloryBoardPointAdjustment;
